import type { CollabCommentData } from '@examdojo/core/tiptap-react-ui-components/lib/collab-config';
import { hasCommentsKit } from '@examdojo/core/tiptap-react-ui-components/lib/has-comments-kit';
import { computePosition, offset } from '@floating-ui/dom';
import type { Editor } from '@tiptap/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useOptionalCollab } from '../../../contexts/collab-context';
import { useOptionalUser } from '../../../contexts/user-context';
import { useTiptapEditor } from '../../../hooks/use-tiptap-editor';
import { useUiEditorState } from '../../../hooks/use-ui-editor-state';
import { MessageSquareIcon } from '../../tiptap-icons/message-square-icon';
import { CommentComposer } from '../comment-composer';
import { resolvePosFromDOM, resolveTopLevelBlockAt } from './resolve-block-at-coords';

const RIGHT_ZONE_RATIO = 0.3;
const BLOCK_HEIGHT_THRESHOLD = 40;
const HIDE_DELAY_MS = 500;

export interface CommentGutterHandleProps {
  editor?: Editor | null;
}

export function CommentGutterHandle({ editor: providedEditor }: CommentGutterHandleProps) {
  const { editor } = useTiptapEditor(providedEditor);
  const user = useOptionalUser();
  const { hasCollab, hasError: collabHasError } = useOptionalCollab();
  const { aiGenerationActive, isDragging } = useUiEditorState(editor);

  const handleRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const pendingCoordsRef = useRef<{ x: number; y: number } | null>(null);
  const currentNodePosRef = useRef<number>(-1);
  const currentNodeDomRef = useRef<HTMLElement | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lockedRef = useRef(false);

  const [composerOpen, setComposerOpen] = useState(false);

  const available = !!user && hasCollab && hasCommentsKit(editor) && !collabHasError;

  const setHandleVisibility = useCallback((visible: boolean) => {
    const el = handleRef.current;
    if (!el) return;
    el.style.opacity = visible ? '1' : '0';
    el.style.pointerEvents = visible ? 'auto' : 'none';
  }, []);

  const reposition = useCallback(
    (dom: HTMLElement) => {
      const el = handleRef.current;
      if (!el || !editor) return;

      const nodeHeight = dom.getBoundingClientRect().height;
      const handleHeight = el.getBoundingClientRect().height || 24;
      const crossAxis = nodeHeight > BLOCK_HEIGHT_THRESHOLD ? 0 : nodeHeight / 2 - handleHeight / 2;

      computePosition(dom, el, {
        placement: 'right-start',
        strategy: 'absolute',
        middleware: [offset({ mainAxis: 0, crossAxis })],
      }).then(({ x, y }) => {
        Object.assign(el.style, { left: `${x}px`, top: `${y}px` });
      });
    },
    [editor],
  );

  useEffect(() => {
    if (!editor?.view?.dom || !available) return;
    const container = editor.view.dom.parentElement;
    if (!container) return;
    const handle = handleRef.current;

    const cancelHideTimer = () => clearTimeout(hideTimerRef.current);

    const hideNow = () => {
      cancelHideTimer();
      currentNodeDomRef.current = null;
      setHandleVisibility(false);
    };

    const hideWithDelay = () => {
      cancelHideTimer();
      hideTimerRef.current = setTimeout(hideNow, HIDE_DELAY_MS);
    };

    const show = (pos: number, dom: HTMLElement) => {
      cancelHideTimer();
      currentNodePosRef.current = pos;
      currentNodeDomRef.current = dom;
      reposition(dom);
      setHandleVisibility(true);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (lockedRef.current) return;
      if (handleRef.current?.contains(e.target as globalThis.Node)) {
        cancelHideTimer();
        return;
      }

      pendingCoordsRef.current = { x: e.clientX, y: e.clientY };
      if (rafRef.current) return;

      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        const coords = pendingCoordsRef.current;
        if (!coords) return;
        pendingCoordsRef.current = null;

        const resolved = resolveTopLevelBlockAt(editor, coords);

        if (resolved) {
          const rect = resolved.dom.getBoundingClientRect();
          if (coords.x >= rect.right - rect.width * RIGHT_ZONE_RATIO) {
            show(resolved.pos, resolved.dom);
            return;
          }
          hideNow();
          return;
        }

        hideWithDelay();
      });
    };

    const onMouseLeave = (e: MouseEvent) => {
      if (lockedRef.current) return;
      if (handleRef.current?.contains(e.relatedTarget as globalThis.Node)) return;
      hideWithDelay();
    };

    const onHandleLeave = (e: MouseEvent) => {
      if (lockedRef.current) return;
      if (container.contains(e.relatedTarget as globalThis.Node)) return;
      hideWithDelay();
    };

    const onKeyDown = () => {
      if (!lockedRef.current) hideNow();
    };

    container.addEventListener('mousemove', onMouseMove);
    container.addEventListener('mouseleave', onMouseLeave);
    container.addEventListener('keydown', onKeyDown);
    handle?.addEventListener('mouseleave', onHandleLeave);

    return () => {
      cancelAnimationFrame(rafRef.current);
      cancelHideTimer();
      rafRef.current = 0;
      container.removeEventListener('mousemove', onMouseMove);
      container.removeEventListener('mouseleave', onMouseLeave);
      container.removeEventListener('keydown', onKeyDown);
      handle?.removeEventListener('mouseleave', onHandleLeave);
    };
  }, [editor, available, setHandleVisibility, reposition]);

  useEffect(() => {
    if ((aiGenerationActive || isDragging) && !lockedRef.current) {
      setHandleVisibility(false);
    }
  }, [aiGenerationActive, isDragging, setHandleVisibility]);

  const openComposer = useCallback(() => {
    if (!editor || currentNodePosRef.current < 0) return;
    lockedRef.current = true;
    setComposerOpen(true);
  }, [editor]);

  const closeComposer = useCallback(() => {
    lockedRef.current = false;
    setComposerOpen(false);
    setHandleVisibility(false);
  }, [setHandleVisibility]);

  const submitComment = useCallback(
    (content: string) => {
      if (!editor || !user || !content) {
        closeComposer();
        return;
      }

      const dom = currentNodeDomRef.current;
      if (!dom) {
        closeComposer();
        return;
      }

      const nodePos = resolvePosFromDOM(editor, dom);
      if (nodePos === null) {
        closeComposer();
        return;
      }

      const didCreateThread = editor
        .chain()
        .focus()
        .setNodeSelection(nodePos)
        .setThread({ content, commentData: { userName: user.name, userAvatar: user.avatar } satisfies CollabCommentData })
        .run();

      if (didCreateThread) {
        closeComposer();
      }
    },
    [editor, user, closeComposer],
  );

  if (!editor || !available) return null;

  const shouldHide = aiGenerationActive || isDragging;

  return (
    <div
      ref={handleRef}
      className="comment-gutter-handle"
      style={{ opacity: 0, position: 'absolute', pointerEvents: 'none' }}
    >
      <button
        type="button"
        className="comment-gutter-btn"
        onClick={openComposer}
        title="Add comment"
        style={shouldHide ? { opacity: 0, pointerEvents: 'none' } : undefined}
      >
        <MessageSquareIcon width={16} height={16} />
      </button>

      {composerOpen && (
        <CommentComposer className="comment-gutter-popover" onSubmit={submitComment} onClose={closeComposer} />
      )}
    </div>
  );
}
